import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AppBar,
  Toolbar,
  Avatar
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Email,
  Store,
  ExpandMore,
  Refresh,
  FilterList,
  ArrowBack
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.sobcof.com';

interface CafeApplication {
  id: string;
  cafeName: string;
  ownerName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  district?: string;
  website?: string;
  instagram?: string;
  cafeType: string;
  capacity: string;
  averagePrice: string;
  specialFeatures?: string[];
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
  adminNotes?: string;
  processedBy?: string;
  processedAt?: string;
  photoURL?: string;
}


const CafeApplicationsPage: React.FC = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<CafeApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalApplications, setTotalApplications] = useState(0);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<CafeApplication | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch applications
  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        sort: 'createdAt',
        order: 'desc'
      });

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await fetch(`${API_BASE_URL}/api/cafe-applications?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setApplications(data.data.applications);
        setTotalApplications(data.data.pagination.totalApplications);
      } else {
        throw new Error(data.message || 'Başvurular alınamadı');
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };


  // Update application status
  const updateApplicationStatus = async () => {
    if (!selectedApplication || !actionType) return;

    try {
      setActionLoading(true);

      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${API_BASE_URL}/api/cafe-applications/${selectedApplication.id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: actionType === 'approve' ? 'approved' : 'rejected',
          adminNotes: adminNotes.trim() || null,
          adminEmail: admin?.email
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Refresh the list
        await fetchApplications();
        
        // Close dialog
        setActionDialogOpen(false);
        setSelectedApplication(null);
        setAdminNotes('');
        setActionType(null);
      } else {
        throw new Error(data.message || 'Status güncellenirken hata oluştu');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err instanceof Error ? err.message : 'Status güncellenirken hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchApplications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter]);


  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewApplication = (application: CafeApplication) => {
    setSelectedApplication(application);
    setViewDialogOpen(true);
  };

  const handleAction = (application: CafeApplication, action: 'approve' | 'reject') => {
    setSelectedApplication(application);
    setActionType(action);
    setAdminNotes('');
    setActionDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Beklemede';
      case 'approved': return 'Onaylandı';
      case 'rejected': return 'Reddedildi';
      default: return status;
    }
  };

  return (
    <>
      {/* Navigation Bar */}
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            mr: 2,
            width: 40,
            height: 40
          }}>
            <Store sx={{ fontSize: 20 }} />
          </Avatar>
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 700, 
              color: 'white',
              letterSpacing: '-0.5px'
            }}>
              Kafe Başvuruları
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.875rem'
            }}>
              Partnerlik başvurularını yönet ve değerlendir
            </Typography>
          </Box>
          <Button
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
            startIcon={<Refresh />}
            onClick={() => {
              fetchApplications();
            }}
            disabled={loading}
          >
            Yenile
          </Button>
        </Toolbar>
      </AppBar>

    <Container maxWidth="xl" sx={{ py: 4 }}>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}


      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ width: { xs: '100%', sm: '300px' } }}>
            <FormControl fullWidth size="small">
              <InputLabel>Durum Filtresi</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                label="Durum Filtresi"
                startAdornment={<FilterList sx={{ mr: 1, color: 'text.secondary' }} />}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="pending">Beklemede</MenuItem>
                <MenuItem value="approved">Onaylandı</MenuItem>
                <MenuItem value="rejected">Reddedildi</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Applications Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Kafe Adı</strong></TableCell>
                <TableCell><strong>Kafe Sahibi</strong></TableCell>
                <TableCell><strong>Şehir</strong></TableCell>
                <TableCell><strong>Tür</strong></TableCell>
                <TableCell><strong>Durum</strong></TableCell>
                <TableCell><strong>Başvuru Tarihi</strong></TableCell>
                <TableCell><strong>İşlemler</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Henüz başvuru bulunmamaktadır
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((application) => (
                  <TableRow key={application.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {application.cafeName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                        {application.cafeType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {application.ownerName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                        {application.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {application.city}{application.district ? ` / ${application.district}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.85rem">
                        {application.capacity} • {application.averagePrice}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(application.status)}
                        color={getStatusColor(application.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.85rem">
                        {new Date(application.createdAt).toLocaleDateString('tr-TR')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Detayları Görüntüle">
                          <IconButton
                            size="small"
                            onClick={() => handleViewApplication(application)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {application.status === 'pending' && (
                          <>
                            <Tooltip title="Onayla">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleAction(application, 'approve')}
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reddet">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleAction(application, 'reject')}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {!loading && (
          <TablePagination
            component="div"
            count={totalApplications}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} / ${count !== -1 ? count : `${to}'dan fazla`}`
            }
          />
        )}
      </Paper>

      {/* View Application Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            🏪 Kafe Başvuru Detayları
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ mt: 2 }}>
              {/* Status */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Chip
                  label={getStatusText(selectedApplication.status)}
                  color={getStatusColor(selectedApplication.status) as any}
                  size="medium"
                />
                <Typography variant="body2" color="text.secondary">
                  Başvuru ID: {selectedApplication.id}
                </Typography>
              </Box>

              {/* Kafe Bilgileri */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    <Store sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Kafe Bilgileri
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Kafe Fotoğrafı */}
                    {selectedApplication.photoURL && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" mb={1}>Kafe Fotoğrafı</Typography>
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <img
                            src={selectedApplication.photoURL}
                            alt={selectedApplication.cafeName}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '400px',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = selectedApplication.photoURL!;
                              link.download = `${selectedApplication.cafeName}-photo.jpg`;
                              link.target = '_blank';
                              link.click();
                            }}
                          >
                            <i className="fas fa-download" style={{ marginRight: '8px' }}></i>
                            Fotoğrafı İndir
                          </Button>
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                        <Typography variant="body2" color="text.secondary">Kafe Adı</Typography>
                        <Typography variant="body1" fontWeight="medium">{selectedApplication.cafeName}</Typography>
                      </Box>
                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                        <Typography variant="body2" color="text.secondary">Kafe Türü</Typography>
                        <Typography variant="body1">{selectedApplication.cafeType}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                        <Typography variant="body2" color="text.secondary">Kapasite</Typography>
                        <Typography variant="body1">{selectedApplication.capacity}</Typography>
                      </Box>
                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                        <Typography variant="body2" color="text.secondary">Fiyat Aralığı</Typography>
                        <Typography variant="body1">{selectedApplication.averagePrice}</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Adres</Typography>
                      <Typography variant="body1">{selectedApplication.address}, {selectedApplication.city}{selectedApplication.district ? ` / ${selectedApplication.district}` : ''}</Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* İletişim Bilgileri */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
                    İletişim Bilgileri
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                        <Typography variant="body2" color="text.secondary">Kafe Sahibi</Typography>
                        <Typography variant="body1" fontWeight="medium">{selectedApplication.ownerName}</Typography>
                      </Box>
                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                        <Typography variant="body2" color="text.secondary">E-posta</Typography>
                        <Typography 
                          variant="body1" 
                          component="a" 
                          href={`mailto:${selectedApplication.email}`}
                          sx={{ textDecoration: 'none', color: 'primary.main' }}
                        >
                          {selectedApplication.email}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                        <Typography variant="body2" color="text.secondary">Telefon</Typography>
                        <Typography 
                          variant="body1"
                          component="a"
                          href={`tel:${selectedApplication.phone}`}
                          sx={{ textDecoration: 'none', color: 'primary.main' }}
                        >
                          {selectedApplication.phone}
                        </Typography>
                      </Box>
                      {selectedApplication.website && (
                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                          <Typography variant="body2" color="text.secondary">Website</Typography>
                          <Typography 
                            variant="body1"
                            component="a"
                            href={selectedApplication.website}
                            target="_blank"
                            sx={{ textDecoration: 'none', color: 'primary.main' }}
                          >
                            {selectedApplication.website}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {selectedApplication.instagram && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Instagram</Typography>
                        <Typography 
                          variant="body1"
                          component="a"
                          href={`https://instagram.com/${selectedApplication.instagram.replace('@', '')}`}
                          target="_blank"
                          sx={{ textDecoration: 'none', color: 'primary.main' }}
                        >
                          {selectedApplication.instagram}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Kafe Özellikleri */}
              {selectedApplication.specialFeatures && selectedApplication.specialFeatures.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Kafe Özellikleri
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedApplication.specialFeatures.map((feature, index) => (
                        <Chip key={index} label={feature} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Ek Mesaj - Eski başvurular için */}
              {(selectedApplication as any).message && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Ek Bilgiler
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Mesaj</Typography>
                      <Typography variant="body1">{(selectedApplication as any).message}</Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Admin Notes */}
              {selectedApplication.adminNotes && (
                <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Admin Notları:
                  </Typography>
                  <Typography variant="body2">{selectedApplication.adminNotes}</Typography>
                  {selectedApplication.processedBy && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                      İşleyen: {selectedApplication.processedBy} • {selectedApplication.processedAt && new Date(selectedApplication.processedAt).toLocaleString('tr-TR')}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Timestamps */}
              <Box mt={3} pt={2} borderTop="1px solid" borderColor="grey.200">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                    <Typography variant="body2" color="text.secondary">Başvuru Tarihi</Typography>
                    <Typography variant="body2">
                      {new Date(selectedApplication.createdAt).toLocaleString('tr-TR')}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                    <Typography variant="body2" color="text.secondary">Son Güncelleme</Typography>
                    <Typography variant="body2">
                      {new Date(selectedApplication.updatedAt).toLocaleString('tr-TR')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Kapat
          </Button>
          {selectedApplication?.status === 'pending' && (
            <>
              <Button
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => {
                  setViewDialogOpen(false);
                  handleAction(selectedApplication, 'approve');
                }}
              >
                Onayla
              </Button>
              <Button
                color="error"
                startIcon={<Cancel />}
                onClick={() => {
                  setViewDialogOpen(false);
                  handleAction(selectedApplication, 'reject');
                }}
              >
                Reddet
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Action Dialog (Approve/Reject) */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? (
            <Typography color="success.main">
              <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
              Başvuruyu Onayla
            </Typography>
          ) : (
            <Typography color="error.main">
              <Cancel sx={{ mr: 1, verticalAlign: 'middle' }} />
              Başvuruyu Reddet
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {selectedApplication && (
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{selectedApplication.cafeName}</strong> başvurusunu {actionType === 'approve' ? 'onaylamak' : 'reddetmek'} üzeresiniz.
                </Typography>
              </Box>
            )}
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label={actionType === 'approve' ? 'Onay Notları (Opsiyonel)' : 'Ret Nedeni'}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder={
                actionType === 'approve' 
                  ? 'Onay ile ilgili ek bilgiler...'
                  : 'Reddedilme nedeni ve öneriler...'
              }
              helperText={
                actionType === 'reject' && 
                'Bu mesaj kafe sahibine e-posta ile gönderilecektir.'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)} disabled={actionLoading}>
            İptal
          </Button>
          <Button
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
            onClick={updateApplicationStatus}
            disabled={actionLoading || (actionType === 'reject' && !adminNotes.trim())}
            startIcon={actionLoading ? <CircularProgress size={16} /> : (actionType === 'approve' ? <CheckCircle /> : <Cancel />)}
          >
            {actionLoading ? 'İşleniyor...' : (actionType === 'approve' ? 'Onayla' : 'Reddet')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
};

export default CafeApplicationsPage;
